import { Component, OnInit, ViewContainerRef, ViewChild, ElementRef, NgZone } from '@angular/core';
import { WordTemplateService } from '../../services/word-template.service';
import { WordTemplate, PlaceholderInfo } from '../../beans/word-template';
import { BlockUiService } from '../../services/blockui.service';

@Component({
	selector: 'app-word-template-admin',
	templateUrl: './word-template-admin.component.html',
	styleUrls: ['./word-template-admin.component.scss']
})
export class WordTemplateAdminComponent implements OnInit {

	templates: WordTemplate[] = [];
	placeholderGroups: any = {};
	selectedTemplate: WordTemplate = null;
	htmlContent: string = '';
	editingName: number = null;
	newName: string = '';
	errorMessage: string = '';
	highlightedKey: string = null;
	private selectionListener: () => void = null;

	@ViewChild('htmlEditor') htmlEditor: ElementRef;

	// Hover preview state
	hoveredTemplate: WordTemplate = null;
	hoveredHtml: string = '';
	previewX: number = 0;
	previewY: number = 0;
	private htmlCache: { [id: number]: string } = {};

	templateTypeLabels = {
		'DELO': 'Дело',
		'GRAZHDANSKA_INSTANCIJA': 'Гражд. Инстанция',
		'NAKAZATELNO': 'Наказателно',
		'IZPULNITELNO': 'Изпълнително'
	};

	constructor(
		private service: WordTemplateService,
		private blockUi: BlockUiService,
		private vcRef: ViewContainerRef,
		private zone: NgZone
	) { }

	ngOnInit() {
		this.loadTemplates();
		this.loadPlaceholders();
	}

	loadTemplates() {
		this.service.findAll().subscribe(
			data => this.templates = data,
			err => this.errorMessage = 'Грешка при зареждане на шаблони'
		);
	}

	loadPlaceholders() {
		this.service.getPlaceholders().subscribe(
			data => this.placeholderGroups = data,
			err => { }
		);
	}

	onFileSelected(event: any) {
		const file: File = event.target.files[0];
		if (!file) return;

		const isZip = file.name.toLowerCase().endsWith('.zip');
		const isDocx = file.name.toLowerCase().endsWith('.docx');

		if (!isZip && !isDocx) {
			alert('Моля, изберете .docx или .zip файл');
			return;
		}

		this.blockUi.start(this.vcRef);

		if (isZip) {
			this.service.uploadZip(file).subscribe(
				(result: any) => {
					this.loadTemplates();
					this.blockUi.stop();

					// Build a single summary message
					let messages: string[] = [];
					if (result.uploaded && result.uploaded.length > 0) {
						messages.push('Качени: ' + result.uploaded.length + ' шаблона');
					}
					if (result.skipped && result.skipped.length > 0) {
						messages.push('\nПропуснати файлове:');
						result.skipped.forEach(s => messages.push('  - ' + s));
					}
					if (result.errors && result.errors.length > 0) {
						messages.push('\nГрешни файлове:');
						result.errors.forEach(e => messages.push('  - ' + e));
					}
					if (messages.length === 0) {
						messages.push('Няма намерени .docx файлове в архива');
					}
					alert(messages.join('\n'));
				},
				err => {
					this.blockUi.stop();
					alert('Грешка при качване');
				}
			);
		} else {
			const templateName = file.name.replace('.docx', '');
			this.service.checkName(templateName).subscribe(resp => {
				if (resp.exists) {
					if (!confirm('Шаблон с име "' + templateName + '" вече съществува. Да бъде заменен?')) {
						this.blockUi.stop();
						event.target.value = '';
						return;
					}
				}
				this.service.uploadDocx(file).subscribe(
					result => {
						this.loadTemplates();
						this.blockUi.stop();
					},
					err => {
						this.blockUi.stop();
						alert('Грешка при качване');
					}
				);
			});
		}

		event.target.value = '';
	}

	startRename(template: WordTemplate) {
		this.editingName = template.id;
		this.newName = template.name;
	}

	saveRename(template: WordTemplate) {
		if (!this.newName || this.newName.trim() === '') return;
		this.service.rename(template.id, this.newName.trim()).subscribe(
			result => {
				this.editingName = null;
				this.loadTemplates();
			},
			err => alert('Грешка при преименуване')
		);
	}

	cancelRename() {
		this.editingName = null;
	}

	deleteTemplate(template: WordTemplate) {
		if (!confirm('Сигурни ли сте, че искате да изтриете шаблон "' + template.name + '"?')) {
			return;
		}
		this.service.delete(template.id).subscribe(
			() => this.loadTemplates(),
			err => alert('Грешка при изтриване')
		);
	}

	downloadTemplate(template: WordTemplate) {
		this.service.downloadTemplate(template.id).subscribe(resp => {
			this.downloadBlob(resp.body, template.name + '.docx');
		});
	}

	editTemplate(template: WordTemplate) {
		if (!confirm('Внимание: при редакция от тук се разваля форматирането на документа. ' +
			'След запис трябва да свалите шаблона, да форматирате в Word и да качите наново, за да е добре форматирано. ' +
			'Сигурни ли сте, че искате да продължите?')) {
			return;
		}
		this.selectedTemplate = template;
		this.startSelectionListener();
		this.blockUi.start(this.vcRef);
		this.service.getHtml(template.id).subscribe(
			data => {
				this.htmlContent = data.html;
				this.blockUi.stop();
			},
			err => {
				this.blockUi.stop();
				alert('Грешка при зареждане на съдържанието');
			}
		);
	}

	saveHtml() {
		if (!this.selectedTemplate) return;
		// Read from the contenteditable div
		if (this.htmlEditor && this.htmlEditor.nativeElement) {
			this.htmlContent = this.htmlEditor.nativeElement.innerHTML;
		}
		this.blockUi.start(this.vcRef);
		this.service.saveHtml(this.selectedTemplate.id, this.htmlContent).subscribe(
			result => {
				this.blockUi.stop();
				this.stopSelectionListener();
				this.selectedTemplate = null;
				this.highlightedKey = null;
				this.loadTemplates();
				alert('Шаблонът е записан');
			},
			err => {
				this.blockUi.stop();
				alert('Грешка при запис');
			}
		);
	}

	cancelEdit() {
		this.stopSelectionListener();
		this.selectedTemplate = null;
		this.htmlContent = '';
		this.highlightedKey = null;
	}

	insertPlaceholder(key: string) {
		const placeholder = '{{' + key + '}}';

		// Try to insert at cursor position in the contenteditable editor
		if (this.htmlEditor && this.htmlEditor.nativeElement) {
			this.htmlEditor.nativeElement.focus();
			const sel = window.getSelection();
			if (sel && sel.rangeCount > 0) {
				const range = sel.getRangeAt(0);
				// Check if cursor is inside our editor
				if (this.htmlEditor.nativeElement.contains(range.commonAncestorContainer)) {
					range.deleteContents();
					const textNode = document.createTextNode(placeholder);
					range.insertNode(textNode);
					// Move cursor after inserted text
					range.setStartAfter(textNode);
					range.setEndAfter(textNode);
					sel.removeAllRanges();
					sel.addRange(range);
					// Update model
					this.htmlContent = this.htmlEditor.nativeElement.innerHTML;
					return;
				}
			}
			// Fallback: append at end
			this.htmlEditor.nativeElement.innerHTML += placeholder;
			this.htmlContent = this.htmlEditor.nativeElement.innerHTML;
		}
	}

	private startSelectionListener() {
		this.stopSelectionListener();
		this.selectionListener = () => {
			const sel = window.getSelection();
			if (!sel || sel.isCollapsed) {
				this.zone.run(() => this.highlightedKey = null);
				return;
			}
			const text = sel.toString().trim();
			// Match {{key}} pattern in selection or surrounding text
			const match = text.match(/\{\{([^}]+)\}\}/);
			if (match) {
				this.zone.run(() => this.highlightedKey = match[1]);
			} else {
				// Check if selection is inside a placeholder by expanding to surrounding text
				if (sel.rangeCount > 0 && this.htmlEditor && this.htmlEditor.nativeElement) {
					const range = sel.getRangeAt(0);
					if (this.htmlEditor.nativeElement.contains(range.commonAncestorContainer)) {
						const container = range.startContainer;
						if (container.nodeType === Node.TEXT_NODE) {
							const fullText = container.textContent || '';
							const pos = range.startOffset;
							const before = fullText.substring(0, pos);
							const after = fullText.substring(range.endOffset);
							const openIdx = before.lastIndexOf('{{');
							const closeIdx = after.indexOf('}}');
							if (openIdx !== -1 && closeIdx !== -1 && before.indexOf('}}', openIdx) === -1) {
								const key = fullText.substring(openIdx + 2, range.endOffset + closeIdx);
								this.zone.run(() => this.highlightedKey = key);
								return;
							}
						}
					}
				}
				this.zone.run(() => this.highlightedKey = null);
			}
		};
		document.addEventListener('selectionchange', this.selectionListener);
	}

	private stopSelectionListener() {
		if (this.selectionListener) {
			document.removeEventListener('selectionchange', this.selectionListener);
			this.selectionListener = null;
		}
	}

	downloadAllZip() {
		this.blockUi.start(this.vcRef);
		this.service.downloadAllZip().subscribe(
			resp => {
				this.blockUi.stop();
				this.downloadBlob(resp.body, 'shabloni.zip');
			},
			err => {
				this.blockUi.stop();
				alert('Грешка при сваляне на ZIP');
			}
		);
	}

	deleteAllTemplates() {
		if (!confirm('Сигурни ли сте? Процесът е необратим!')) {
			return;
		}
		const confirmation = prompt('Въведете ИЗТРИЙ за потвърждение:');
		if (confirmation !== 'ИЗТРИЙ') {
			alert('Операцията е отменена.');
			return;
		}
		this.blockUi.start(this.vcRef);
		this.service.deleteAll().subscribe(
			() => {
				this.blockUi.stop();
				this.loadTemplates();
				alert('Всички шаблони бяха изтрити.');
			},
			err => {
				this.blockUi.stop();
				alert('Грешка при изтриване на шаблони');
			}
		);
	}

	getTypeLabel(categories: string): string {
		if (!categories) return '—';
		return categories.split(',')
			.map(c => this.templateTypeLabels[c.trim()] || c.trim())
			.join(', ');
	}

	getPlaceholderCategories(): string[] {
		return Object.keys(this.placeholderGroups);
	}

	// =====================
	// HOVER PREVIEW на шаблона
	// =====================

	onTemplateHover(t: WordTemplate, evt: MouseEvent) {
		this.hoveredTemplate = t;
		this.positionPreview(evt);

		if (this.htmlCache[t.id] !== undefined) {
			this.hoveredHtml = this.htmlCache[t.id];
			return;
		}
		this.hoveredHtml = '<i style="color:#94a3b8">Зарежда се…</i>';
		this.service.getHtml(t.id).subscribe(
			data => {
				this.htmlCache[t.id] = data.html || '';
				if (this.hoveredTemplate && this.hoveredTemplate.id === t.id) {
					this.hoveredHtml = this.htmlCache[t.id];
				}
			},
			err => {
				this.htmlCache[t.id] = '<i style="color:#dc2626">Грешка при зареждане</i>';
				if (this.hoveredTemplate && this.hoveredTemplate.id === t.id) {
					this.hoveredHtml = this.htmlCache[t.id];
				}
			}
		);
	}

	onTemplateHoverMove(evt: MouseEvent) {
		if (this.hoveredTemplate) {
			this.positionPreview(evt);
		}
	}

	onTemplateHoverLeave() {
		this.hoveredTemplate = null;
		this.hoveredHtml = '';
	}

	private positionPreview(evt: MouseEvent) {
		const OFFSET_X = 18;
		const OFFSET_Y = 12;
		const POPOVER_W = 640;
		const POPOVER_H = 520;
		let x = evt.clientX + OFFSET_X;
		let y = evt.clientY + OFFSET_Y;
		if (x + POPOVER_W > window.innerWidth) {
			x = evt.clientX - POPOVER_W - OFFSET_X;
		}
		if (y + POPOVER_H > window.innerHeight) {
			y = window.innerHeight - POPOVER_H - 10;
		}
		this.previewX = Math.max(10, x);
		this.previewY = Math.max(10, y);
	}

	private downloadBlob(blob: Blob, filename: string) {
		const url = window.URL.createObjectURL(blob);
		const a = document.createElement('a');
		a.href = url;
		a.download = filename;
		a.click();
		window.URL.revokeObjectURL(url);
	}
}
